// src/pages/customers/AddCustomer.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import API_BASE_URL from "../../utils/config";

export default function AddOrEditCustomer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // -------- UI state --------
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [dupCheck, setDupCheck] = useState({ checking: false, exists: false, matches: [] });
  const [serverError, setServerError] = useState("");
  const dupTimerRef = useRef(null);

  // geo
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");

  // -------- Form state --------
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    gstin: "",
    dl_no: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
    lat: "",
    lng: "",
    notes: "",
    doctor_id: null,            // now OPTIONAL
    doctor_name: "",
    customer_type: "medical",   // medical | clinic | hospital
    is_active: true,
  });

  // -------- Helpers --------
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const setF = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === "checkbox" ? checked : value;
    if (name === "gstin") v = v.toUpperCase().replace(/\s+/g, "");
    if (name === "phone") v = v.replace(/[^\d]/g, "").slice(0, 10);
    if (name === "pincode") v = v.replace(/[^\d]/g, "").slice(0, 6);
    setF({ [name]: v });

    if (["name", "phone", "gstin"].includes(name)) {
      scheduleDuplicateCheck({ ...form, [name]: v });
    }
  };

  // Geolocation helper
  const useCurrentLocation = () => {
    setGeoError("");
    if (!("geolocation" in navigator)) {
      setGeoError("Geolocation is not supported in this browser.");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords || {};
        setF({ lat: String(latitude ?? ""), lng: String(longitude ?? "") });
        setGeoLoading(false);
      },
      (err) => {
        const msg =
          err.code === 1
            ? "Location permission denied."
            : err.code === 2
            ? "Position unavailable."
            : err.code === 3
            ? "Location request timed out."
            : "Failed to get location.";
        setGeoError(msg);
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Async doctor search (expects [{id,name}])
  const loadDoctorOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 1) return [];
    const res = await axios.get(
      `${API_BASE_URL}/api/doctors/search?q=${encodeURIComponent(inputValue.trim())}`,
      authHeader
    );
    return (res.data || []).map((d) => ({ value: d.id, label: d.name }));
  };

  // Preload for edit
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!id) return setLoading(false);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/customers/${id}`, authHeader);
        if (!mounted) return;
        const c = res.data || {};
        setForm({
          name: c.name || "",
          phone: c.phone || "",
          email: c.email || "",
          gstin: c.gstin || c.gst || "",
          dl_no: c.dl_no || c.dl || "",
          address1: c.address1 || c.address || "",
          address2: c.address2 || "",
          city: c.city || "",
          state: c.state || "",
          pincode: c.pincode || "",
          lat: c.lat ? String(c.lat) : "",
          lng: c.lng ? String(c.lng) : "",
          notes: c.notes || "",
          doctor_id: c.doctor_id ?? null,
          doctor_name: c.doctor_name || "",
          customer_type: c.customer_type || "medical",
          is_active: c.is_active !== false,
        });
      } catch (e) {
        setServerError(e?.response?.data?.message || "Failed to load customer.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Duplicate check (debounced)
  const scheduleDuplicateCheck = (payload) => {
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    dupTimerRef.current = setTimeout(() => doDuplicateCheck(payload), 450);
  };
  const doDuplicateCheck = async (payload) => {
    const { name, phone, gstin } = payload;
    if (!name && !phone && !gstin) return;
    try {
      setDupCheck((d) => ({ ...d, checking: true }));
      const res = await axios.get(`${API_BASE_URL}/api/customers/check-duplicate`, {
        ...authHeader,
        params: { name: name?.trim(), phone: phone?.trim(), gstin: gstin?.trim(), ignoreId: id || undefined },
      });
      setDupCheck({
        checking: false,
        exists: !!res.data?.exists,
        matches: res.data?.matches || [],
      });
    } catch {
      setDupCheck({ checking: false, exists: false, matches: [] });
    }
  };

  // Client validations (doctor_id removed)
  const validate = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = "Customer name is required.";
    if (form.phone && form.phone.length !== 10) errs.phone = "Phone must be 10 digits.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Invalid email.";
    if (form.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gstin))
      errs.gstin = "Invalid GSTIN format.";
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) errs.pincode = "PIN must be 6 digits.";
    if (form.lat && isNaN(Number(form.lat))) errs.lat = "Latitude must be a number.";
    if (form.lng && isNaN(Number(form.lng))) errs.lng = "Longitude must be a number.";
    return errs;
  };

  const [errors, setErrors] = useState({});

  const submit = async (e, { goToDetail = false } = {}) => {
    e?.preventDefault();
    setServerError("");
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) return;

    if (dupCheck.exists && !id) {
      setServerError("Possible duplicate found. Review matches below or proceed to save if intentional.");
      return;
    }

    const payload = {
      name: form.name?.trim(),
      phone: form.phone?.trim() || null,
      email: form.email?.trim() || null,
      gstin: form.gstin?.trim() || null,
      dl_no: form.dl_no?.trim() || null,
      address1: form.address1?.trim() || null,
      address2: form.address2?.trim() || null,
      city: form.city?.trim() || null,
      state: form.state?.trim() || null,
      pincode: form.pincode?.trim() || null,
      lat: form.lat ? Number(form.lat) : null,
      lng: form.lng ? Number(form.lng) : null,
      notes: form.notes?.trim() || null,
      doctor_id: form.doctor_id, // optional
      customer_type: form.customer_type,
      is_active: !!form.is_active,
    };

    try {
      setSaving(true);
      if (id) {
        await axios.put(`${API_BASE_URL}/api/customers/${id}`, payload, authHeader);
        if (goToDetail) navigate(`/customers/${id}`);
        else navigate("/customers");
      } else {
        const res = await axios.post(`${API_BASE_URL}/api/customers`, payload, authHeader);
        const newId = res.data?.id;
        if (goToDetail && newId) navigate(`/customers/${newId}`);
        else navigate("/customers");
      }
    } catch (e2) {
      setServerError(e2?.response?.data?.message || "Failed to save customer.");
    } finally {
      setSaving(false);
    }
  };

  const suppressEnter = (e) => { if (e.key === "Enter") e.preventDefault(); };

  const doctorDefault =
    form.doctor_id && form.doctor_name
      ? { value: form.doctor_id, label: form.doctor_name }
      : undefined;

  return (
    <div className="px-4 py-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">{id ? "Edit Customer" : "Add Customer"}</h1>
          <p className="text-sm text-gray-500 mt-1">
            Doctor is optional at this step — you can link on the Info page later.
          </p>
        </div>

        {(serverError || (dupCheck.exists && !id)) && (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
            <div className="font-medium mb-1">
              {serverError || "Potential duplicate customers detected."}
            </div>
            {dupCheck.matches?.length ? (
              <ul className="list-disc list-inside text-sm">
                {dupCheck.matches.map((m) => (
                  <li key={m.id}>
                    <button
                      onClick={() => navigate(`/customers/${m.id}`)}
                      className="underline hover:no-underline"
                    >
                      {m.name}
                    </button>
                    {m.phone ? ` • ${m.phone}` : ""} {m.gstin ? ` • ${m.gstin}` : ""}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="text-xs mt-2">Not able to save, contact Admin.</div>
          </div>
        )}

        <form onSubmit={(e) => submit(e)} onKeyDown={suppressEnter}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white rounded-xl shadow-sm p-4 md:p-6">
            {/* Left column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium">
                  Customer Name <span className="text-red-600">*</span>
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 ${
                    errors.name ? "border-red-500 ring-red-200" : "border-gray-300 ring-blue-200"
                  }`}
                  placeholder="Agarwal Medical Stores"
                  autoFocus
                />
                {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium">
                  Link Doctor <span className="text-gray-400">(optional)</span>
                </label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadDoctorOptions}
                  defaultValue={doctorDefault}
                  onChange={(opt) => setF({ doctor_id: opt?.value || null, doctor_name: opt?.label || "" })}
                  isClearable
                  placeholder="Search doctor by name…"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium">Phone</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className={`mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.phone ? "border-red-500 ring-red-200" : "border-gray-300 ring-blue-200"
                    }`}
                    placeholder="10-digit mobile"
                  />
                  {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium">Email</label>
                  <input
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    className={`mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.email ? "border-red-500 ring-red-200" : "border-gray-300 ring-blue-200"
                    }`}
                    placeholder="accounts@shop.com"
                  />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium">GSTIN</label>
                  <input
                    name="gstin"
                    value={form.gstin}
                    onChange={onChange}
                    maxLength={15}
                    className={`mt-1 w-full rounded-lg border px-3 py-2 tracking-widest uppercase focus:outline-none focus:ring-2 ${
                      errors.gstin ? "border-red-500 ring-red-200" : "border-gray-300 ring-blue-200"
                    }`}
                    placeholder="08ABCDE1234F1Z5"
                  />
                  {errors.gstin && <p className="text-xs text-red-600 mt-1">{errors.gstin}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium">Drug License No. (DL)</label>
                  <input
                    name="dl_no"
                    value={form.dl_no}
                    onChange={onChange}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 ring-blue-200"
                    placeholder="1559-60"
                  />
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium">Address Line 1</label>
                <input
                  name="address1"
                  value={form.address1}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 ring-blue-200"
                  placeholder="Shop, Street, Landmark"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Address Line 2</label>
                <input
                  name="address2"
                  value={form.address2}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 ring-blue-200"
                  placeholder="Area / Locality"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium">City</label>
                  <input
                    name="city"
                    value={form.city}
                    onChange={onChange}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 ring-blue-200"
                    placeholder="Bhilwara"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">State</label>
                  <input
                    name="state"
                    value={form.state}
                    onChange={onChange}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 ring-blue-200"
                    placeholder="Rajasthan"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">PIN Code</label>
                  <input
                    name="pincode"
                    value={form.pincode}
                    onChange={onChange}
                    maxLength={6}
                    className={`mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.pincode ? "border-red-500 ring-red-200" : "border-gray-300 ring-blue-200"
                    }`}
                    placeholder="311001"
                  />
                  {errors.pincode && <p className="text-xs text-red-600 mt-1">{errors.pincode}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium">Type</label>
                  <select
                    name="customer_type"
                    value={form.customer_type}
                    onChange={onChange}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 bg-white"
                  >
                    <option value="medical">Medical</option>
                    <option value="clinic">Clinic</option>
                    <option value="hospital">Hospital</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium">Latitude</label>
                    <button
                      type="button"
                      onClick={useCurrentLocation}
                      disabled={geoLoading}
                      className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-60"
                      title="Use current location"
                    >
                      {geoLoading ? "Getting…" : "Use current location"}
                    </button>
                  </div>
                  <input
                    name="lat"
                    value={form.lat}
                    onChange={onChange}
                    className={`mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.lat ? "border-red-500 ring-red-200" : "border-gray-300 ring-blue-200"
                    }`}
                    placeholder="25.321"
                  />
                  {errors.lat && <p className="text-xs text-red-600 mt-1">{errors.lat}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium">Longitude</label>
                  <input
                    name="lng"
                    value={form.lng}
                    onChange={onChange}
                    className={`mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 ${
                      errors.lng ? "border-red-500 ring-red-200" : "border-gray-300 ring-blue-200"
                    }`}
                    placeholder="74.639"
                  />
                  {errors.lng && <p className="text-xs text-red-600 mt-1">{errors.lng}</p>}
                </div>
              </div>

              {geoError ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                  {geoError} Tip: Works on HTTPS or http://localhost only.
                </p>
              ) : null}

              <div>
                <label className="block text-sm font-medium">Notes</label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={onChange}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 ring-blue-200"
                  placeholder="Any special terms, timings, delivery hints, etc."
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  id="is_active"
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={onChange}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="is_active" className="text-sm">Active</label>
              </div>
            </div>
          </div>

          {/* Sticky actions */}
          <div className="sticky bottom-0 left-0 right-0 mt-6 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t">
            <div className="max-w-5xl mx-auto py-3 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => navigate("/customers")}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => submit(e, { goToDetail: true })}
                disabled={saving}
                className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {id ? "Save & View" : "Save & View"}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-60"
              >
                {saving ? "Saving..." : id ? "Save Changes" : "Add Customer"}
              </button>
            </div>
          </div>
        </form>

        {loading && (
          <div className="fixed inset-0 bg-white/60 backdrop-blur-sm grid place-items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-300 border-t-blue-600" />
          </div>
        )}
      </div>
    </div>
  );
}
